// Persisted "active trip" selection (per-user, localStorage). Lets the user
// move between trips they belong to without changing any server contract:
// getMyTrip() reads this key and resolves to the matching membership, or
// falls back to the most-recently-joined trip (legacy behavior).
//
// OTA-safe: web/frontend only. No native/plugin/schema changes.

import { useEffect, useState } from "react";

export const ACTIVE_TRIP_EVENT = "roava:active-trip-changed";

function keyFor(userId: string): string {
  return `roava:activeTrip:${userId}`;
}

/** Read the active trip id for a user. Returns null when unset or in SSR. */
export function getActiveTripId(userId: string | null | undefined): string | null {
  if (!userId) return null;
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(keyFor(userId));
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

/** Persist (or clear with null) the active trip id and notify listeners. */
export function setActiveTripId(userId: string | null | undefined, tripId: string | null): void {
  if (!userId) return;
  if (typeof window === "undefined") return;
  try {
    if (tripId) window.localStorage.setItem(keyFor(userId), tripId);
    else window.localStorage.removeItem(keyFor(userId));
    window.dispatchEvent(
      new CustomEvent(ACTIVE_TRIP_EVENT, { detail: { userId, tripId } }),
    );
  } catch {
    /* ignore */
  }
}

/** Clear the active-trip selection for a user (sign-out helper). */
export function clearActiveTripFor(userId: string | null | undefined): void {
  setActiveTripId(userId, null);
}

/** Reactive hook: returns the current active trip id and re-renders on change. */
export function useActiveTripId(userId: string | null | undefined): string | null {
  const [val, setVal] = useState<string | null>(() => getActiveTripId(userId));
  useEffect(() => {
    setVal(getActiveTripId(userId));
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail as { userId?: string } | undefined;
      if (!userId || !detail?.userId || detail.userId === userId) {
        setVal(getActiveTripId(userId));
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (!userId) return;
      if (e.key === keyFor(userId)) setVal(getActiveTripId(userId));
    };
    window.addEventListener(ACTIVE_TRIP_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(ACTIVE_TRIP_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, [userId]);
  return val;
}
